import { AfterViewChecked, ChangeDetectorRef, Component, ComponentFactoryResolver, Directive, ElementRef, HostListener, Input, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { ApiService } from 'app/api.service';
import { Pagination } from 'app/shared/pagination';
import { AppService } from 'app/app.service';
import { UITreeData, UITreeNodeAction, UITreeNodeProgress } from 'app/ui/ui-tree.component';
import { ModalEditProductComponent } from '../modal-edit-product/modal-edit-product.component';
import { DeferredAction } from 'app/shared/deferred-action';
import { ProductSearch } from '../product-search/product-search';

declare const Mangler: any;

@Directive({
	selector: '[modalEditProduct]',
})
export class ModalEditProductDirective {
	constructor(public viewContainerRef: ViewContainerRef) { }
}

class CategoryTree implements UITreeData {

	data;
	groups;
	productSelectModal: ModalSelectProductComponent;

	allCategories = { id: 0, description: 'All Category Groups', type: '', children: [] };

	constructor(data, productSelectModal: ModalSelectProductComponent) {
		this.data = data;
		this.productSelectModal = productSelectModal;
		this.refreshData();
	}

	refreshData() {
		// Add categories to category groups
		if (this.data.groups && this.data.groups.length > 0) {
			this.data.groups.forEach(cg => {
				cg.categories = Mangler.find(this.data.categories, { category_group_id: cg.id });
			});

			this.groups = this.data.groups.slice();
			this.groups.unshift(this.allCategories);
		}
	}

	isGroup(node: any): boolean {
		if (!node || node === this.allCategories) return false;
		if (this.groups.indexOf(node) !== -1) return true;
		return false;
	}

	isCategory(node: any): boolean {
		if (!node || node === this.allCategories) return false;
		if (this.groups.indexOf(node) !== -1) return false;
		return true;
	}

	hasChildren(node: any): boolean { return this.isGroup(node) ? !!node.categories.length : false; }
	getChildren(node: any): any[] { return node ? (this.isGroup(node) ? node.categories : []) : this.groups; }
	getParent(node: any) { return node.category_group_id ? Mangler.findOne(this.groups, { id: node.category_group_id }) : null; }

	getActions(_node: any): UITreeNodeAction[] { return []; }

	getDescription(node: any): string { return node.description; }

	getInfo(_node: any): string { return ''; }

	getDescriptionClass(node: any): string { return (this.isCategory(node) ? 'text-normal' : '') + (node === this.allCategories ? ' text-italic' : ''); }

	getInfoClass(_node: any): string { return ''; }

	hasProgress(_node: any): boolean { return false; }
	getProgress(_node: any): UITreeNodeProgress[] { return []; }

	isExpanded(node: any): boolean { return this.isGroup(node) && this.productSelectModal.isCategoryGroupExpanded(node.id); }

	isSelected(node: any): boolean {
		if (node === this.allCategories) return this.productSelectModal.filterCategoryId === null && this.productSelectModal.filterCategoryGroupId === null;
		if (this.isGroup(node)) return this.productSelectModal.filterCategoryGroupId === node.id;
		if (this.isCategory(node)) return this.productSelectModal.filterCategoryId === node.id;
		return false;
	}

	canHover(_node: any): boolean { return true; }
	canSelect(_node: any): boolean { return true; }
	canDrag(_node: any): boolean { return false; }
	canDrop(_node: any, _targetParent: any, _targetPosition: number): boolean { return false; }

	expand(node: any) { if (this.isGroup(node)) this.productSelectModal.setCategoryGroupExpanded(node.id, true); }
	collapse(node: any) { if (this.isGroup(node)) this.productSelectModal.setCategoryGroupExpanded(node.id, false); }

	select(node: any, _clicked: boolean) {
		if (this.isGroup(node)) {
			this.productSelectModal.selectGroup(node);
		} else if (this.isCategory(node)) {
			this.productSelectModal.selectCategory(node);
		} else {
			this.productSelectModal.viewAll();
		}
	}

	action(_node: any, _action: UITreeNodeAction) { }

	drop(_node: any, _targetParent: any, _targetPosition: number) { }

}

@Component({
	selector: 'modal-select-product',
	templateUrl: './modal-select-product.component.html',
	styleUrls: ['./modal-select-product.component.scss']
})
export class ModalSelectProductComponent extends UIModalBaseComponent implements AfterViewChecked, OnDestroy {

	@ViewChild(ModalEditProductDirective, { static: true }) editProductHost: ModalEditProductDirective;

	@ViewChild('globalSearchBox') globalSearchBox: ElementRef;

	@Input() addedProducts = [];

	addAccessory = false;
	categoryGroups: any[] = [];
	categories: any[] = [];
	selectedCategory = null;
	addToToolkit = false;
	categoryTree: CategoryTree;
	categoryTreeKey = null;
	cachedCategories = [];
	cachedGroups = [];
	data;
	replacementProduct = null;

	/**
	 * Where products are being listed from. `selected` is a pseudo-source: the basket view of what has been
	 * picked so far in `multi` mode, rather than a place products are read from.
	 */
	sources = [
		{ id: 'global-search', description: 'Global Search', icon: 'wq wq-search', tutorial: null, show: false },
		{ id: 'my-products', description: 'My Products', icon: 'wq wq-cart-outline', tutorial: null, show: true },
		{ id: 'my-catalogues', description: 'My Catalogues', icon: 'wq wq-catalogue', tutorial: 504, show: false },
		{ id: 'linked-catalogues', description: 'Linked Catalogues', icon: 'wq wq-catalogue', tutorial: 504, show: true }
	];
	// Null until the first open picks one, so a fresh picker lands on the global search where it's available.
	source = null;

	tabs: any = {
		all_products: { id: 'all_products', title: 'Simple Products', show: true, icon: 'wq wq-product', color: 'success' },
		bundles: { id: 'bundles', title: 'Configurable Products', show: true, icon: 'wq wq-customer', color: 'info' },
		options: { id: 'options', title: 'Options', show: false, icon: 'wq wq-radio-checked', color: 'danger' }
	};

	tabList = [];
	activeTab = 'all_products';
	list: any[] = null;
	catalogues: any[] = null;
	selectedCatalogue = null;
	search = '';
	pagination = new Pagination();

	filterCategoryId = null;
	filterCategoryGroupId = null;

	multiSelectList = [];
	forCatalogue = false;
	costPriceField = null;
	sellPriceField = null;

	_collapsed = true;
	collapsedExceptions = [];

	expandedCategoryGroups = [];

	quoteId = null;

	/**
	 * What the picker is for. It decides what a clicked product does and what the modal closes with:
	 *
	 *   single            closes straight away with the raw product row
	 *   multi             toggles the product in multiSelectList; the footer closes with the array
	 *   replace           holds the product as the replacement; the footer closes with it
	 *   quote-line        closes with { product, category }
	 *   quote-line-multi  emits { product, category } without dismissing, so several can be added in one trip
	 *
	 * `quote-line-multi` isn't something a caller asks for - open() promotes `quote-line` to it when the
	 * organisation has multi_select_product on.
	 */
	mode = 'single';

	/**
	 * Searches everything the organisation can see - its own products and its linked catalogues - rather than one
	 * source at a time. Built on the first open that offers it, and kept afterwards so the term, the filters and
	 * the category tree survive the modal closing.
	 */
	productSearch: ProductSearch = null;
	globalSearchAllowed = false;

	// Product ids being read back after being picked from the global search, so a double click adds once.
	resolvingProducts = [];

	// How many catalogues the organisation owns, once counted. Null until then - see resolveOwnCatalogues.
	ownCatalogueCount = null;

	refreshAction = new DeferredAction();

	/**
	 * Product ids that are already in, as the global search results should show them. What counts as "in" depends
	 * on the mode: the basket in `multi`, the held replacement in `replace`, and whatever the host reports having
	 * added in `quote-line-multi`.
	 */
	get pickedProductIds() {
		if (this.isMulti) return this.multiSelectList.map(item => item.id);
		if (this.isReplace) return this.replacementProduct ? [this.replacementProduct.id] : [];
		return (this.addedProducts || []).map(item => item.id);
	}

	get isMulti() { return this.mode === 'multi'; }

	get isReplace() { return this.mode === 'replace'; }

	get isQuoteLine() { return this.mode === 'quote-line' || this.mode === 'quote-line-multi'; }

	// The one mode that leaves the modal up after a product is picked.
	get staysOpen() { return this.mode === 'quote-line-multi'; }

	get collapsed() { return this._collapsed; }
	set collapsed(value) {
		this._collapsed = value;
		this.collapsedExceptions = [];
		this.refresh();
	}

	get expand() { return !this.collapsed; }
	set expand(value) { this.collapsed = !value; }

	constructor(
		public app: AppService,
		private api: ApiService,
		private cdr: ChangeDetectorRef,
		private factory: ComponentFactoryResolver
	) {
		super();
	}

	ngAfterViewChecked() {
		this.cdr.detectChanges();
	}

	ngOnDestroy(): void {
		this.refreshAction.cancel();
	}

	// Recent searches and category suggestions hang off the search box, so anything clicked elsewhere dismisses them.
	@HostListener('document:click', ['$event'])
	onDocumentClick(event) {
		if (!this.productSearch || this.globalSearchBox?.nativeElement.contains(event.target)) return;

		this.productSearch.showRecentSearches = false;
		this.productSearch.suggestions = [];
	}

	open(data: any) {
		this.mode = data?.mode || 'single';

		// Adding line after line without the modal closing is an organisation-level choice, not the caller's.
		if (this.mode === 'quote-line' && this.app.orgInfo?.multi_select_product) this.mode = 'quote-line-multi';

		this.replacementProduct = null;
		this.multiSelectList = [];
		this.resolvingProducts = [];
		this.addToToolkit = false;
		this.forCatalogue = !!(data?.for_catalogue);
		this.costPriceField = data?.cost_price_field || null;
		this.sellPriceField = data?.sell_price_field || null;

		this.categoryGroups = data?.categoryGroupList || [];
		this.categories = data?.categoryList || [];
		this.selectedCategory = data?.category || null;
		this.addAccessory = data?.accessory || (data.is_bundle === 0 || data.is_bundle === false);
		this.quoteId = data?.quote_id || null;

		this.tabList = [];
		this.tabs.options.show = !!(data?.show_options);
		this.tabs.bundles.show = !this.addAccessory;
		this.tabList.push(this.tabs.all_products);
		this.tabList.push(this.tabs.bundles);
		this.tabList.push(this.tabs.options);

		// Check if the active tab is in the valid tab list
		let found = false;
		Mangler.each(this.tabList, (k, t) => {
			if (t.show && this.activeTab === t.id) found = true;
		});

		if (!found) {
			// Select the first tab as active
			this.activeTab = null;
			Mangler.each(this.tabList, (k, t) => {
				if (t.show && !this.activeTab) this.activeTab = t.id;
			});
		}

		const oldSearch = this.search;
		if (Mangler.isObject(data) && data.hasOwnProperty('search')) this.search = data?.search || '';

		// Add categories to category groups
		this.categoryGroups.forEach(g => g.categories = []);
		const categoryGroupIndex = Mangler.index(this.categoryGroups, 'id');
		this.categories.forEach(c => {
			const cg = categoryGroupIndex[c.category_group_id];
			cg?.categories.push(c);
		});

		if (this.forCatalogue) {
			// If called with for_catalogue, you can only browse your own products, no third party ones.
			this.list = null;
			this.source = 'my-products';
		}

		this.globalSearchAllowed = !!this.app.orgInfo?.allow_global_search && !this.forCatalogue;

		const globalSearch = this.sources.find(source => source.id === 'global-search');
		if (globalSearch) globalSearch.show = this.globalSearchAllowed;

		const linkedCatalogues = this.sources.find(source => source.id === 'linked-catalogues');
		if (linkedCatalogues) linkedCatalogues.show = !this.forCatalogue;

		// Which sources are on offer is settled here, whichever one the modal happens to open on.
		this.resolveOwnCatalogues();

		if (this.globalSearchAllowed) {
			if (!this.productSearch) this.productSearch = new ProductSearch(this.api, this.app);

			// The host's limits, so the search only offers products it can actually take.
			this.productSearch.constraints = { sold_to_customer: 1, is_bundle: this.addAccessory ? 0 : null };
			if (Mangler.isObject(data) && data.hasOwnProperty('search')) this.productSearch.query = data?.search || '';
		}

		// Where a picker with nothing remembered opens: the global search where it is on offer, unless the caller
		// asks for somewhere else. The CSV mapper does - it seeds a part number meant to be matched against the
		// organisation's own products, not the whole catalogue.
		let firstSource = data?.source || (this.globalSearchAllowed ? 'global-search' : 'my-products');
		if (!this.isSourceVisible(firstSource)) firstSource = 'my-products';

		// Whatever was last used, unless it isn't on offer this time - which also covers the basket view, since
		// that lists the current pick rather than being somewhere products come from.
		if (!this.isSourceVisible(this.source)) {
			this.selectSource(firstSource);
		} else if (this.source === 'global-search') {
			this.productSearch.loadBrowse();
			this.productSearch.run();
		} else {
			if (!this.data || this.search !== oldSearch) this.refresh();
		}

		if (this.source === 'linked-catalogues' || this.source === 'my-catalogues') {
			this.app.tutorial?.validateStep(504);
		} else {
			this.app.tutorial?.invalidateStep(504);
		}

		this.modal.open();
	}

	openNewProductModal() {
		// Dynamically load a new instance of ModalEditProductComponent to avoid a circular reference
		const editorFactory = this.factory.resolveComponentFactory(ModalEditProductComponent);
		const viewContainerRef = this.editProductHost.viewContainerRef;
		viewContainerRef.clear();
		const componentRef = viewContainerRef.createComponent<ModalEditProductComponent>(editorFactory);

		componentRef.instance.close.subscribe(value => {
			if (value?.id) {
				const options: any = {
					product_id: value.id,
					include_categories: 0
				};

				if (this.costPriceField) options.cost_price_field = this.costPriceField;
				if (this.sellPriceField) options.sell_price_field = this.sellPriceField;
				if (this.quoteId) options.quote_id = this.quoteId;

				this.api.product.list(options, data => {
					if (data.list.length) this.selectItem(data.list[0]);
					if (this.isQuoteLine || this.isMulti) this.refreshProducts();
				}, error => {
					this.app.notifications.showDanger(error.message);
				});
			}
			componentRef.destroy();
		});
		componentRef.instance.open({});
	}

	catalogueChange() {
		this.data = null;
		this.filterCategoryGroupId = null;
		this.filterCategoryId = null;
		this.refresh();
	}

	refresh() {
		if (this.catalogues) {
			this.refreshProducts();
		} else {
			if (this.source === 'linked-catalogues') {
				this.api.catalogue.listApprovedCatalogues(list => {
					this.catalogues = list;
					this.refreshProducts();
				});
			} else {
				this.api.catalogue.listMyCatalogues(list => {
					this.catalogues = list;
					this.setOwnCatalogueCount(this.catalogues.length);
					this.refreshProducts();
				});
			}
		}
	}

	tabChange(tab) {
		if (!tab.disabled) {
			this.activeTab = tab.id;
			this.refreshProducts();
		}
	}

	refreshProducts(delay = 0) {
		const options: any = {
			sold_to_customer: 1,
			mode: this.source,
			catalogue_id: this.selectedCatalogue?.id
		};

		if (this.addAccessory) options.is_bundle = 0;

		if (this.forCatalogue && this.source === 'my-products') options.uncatalogued = 1;
		if (this.costPriceField) options.cost_price_field = this.costPriceField;
		if (this.sellPriceField) options.sell_price_field = this.sellPriceField;

		if (this.filterCategoryId) options.category_id = this.filterCategoryId;
		if (!this.filterCategoryId && this.filterCategoryGroupId) options.category_group_id = this.filterCategoryGroupId;

		if (this.activeTab === 'bundles') options.bundle = 1;
		if (this.quoteId) options.quote_id = this.quoteId;

		options['paginated'] = 1;
		options['page_size'] = this.pagination.pageSize;
		options['page_number'] = this.pagination.page;
		options['keywords'] = this.search;

		// The category tree only depends on the source and the selected catalogue, never on keywords, paging or
		// category filters, so reuse it instead of making the API rebuild it on every request.
		const categoryKey = this.source + '|' + (this.selectedCatalogue?.id || '');
		const includeCategories = categoryKey !== this.categoryTreeKey;
		if (!includeCategories) options.include_categories = 0;

		const deferredCount = this.refreshAction.run(delay, () => {
			this.refreshAction.wait();

			this.api.product.list(options, data => {
				this.refreshAction.release();
				if (deferredCount === this.refreshAction.count) {
					if (includeCategories) {
						this.categoryTreeKey = categoryKey;
						this.cachedCategories = data.categories || [];
						this.cachedGroups = data.groups || [];
					} else {
						data.categories = this.cachedCategories;
						data.groups = this.cachedGroups;
					}
					this.data = data;
					this.pagination.applyData(data.list);
					this.categoryTree = new CategoryTree(this.data, this);
				}
			}, () => {
				this.refreshAction.release();
			});
		});
	}

	selectItem(item) {
		if (!item) return;

		const closeModal = () => {
			this.app.tutorial?.validateStep(505);
			this.modal.close({
				product: item,
				category: this.selectedCategory
			});
		};

		switch (this.mode) {
			case 'quote-line':
			case 'quote-line-multi':
				if (this.addToToolkit && this.selectedCategory !== null) {
					// The toolkit is a one-product-at-a-time operation, so it closes even in the multi mode.
					this.api.product.addToToolkit({
						category_id: this.selectedCategory,
						product_id: item.id
					}, closeModal, error => {
						this.app.notifications.showDanger(error.message);
					});
				} else if (this.staysOpen) {
					// Reported without dismissing, so the next product can be picked straight away.
					this.modal.onClose.emit({ product: item, category: this.selectedCategory });
				} else {
					closeModal();
				}
				break;

			case 'multi': {
				const found = Mangler.findOne(this.multiSelectList, { id: item.id });
				if (found) {
					const i = this.multiSelectList.indexOf(found);
					if (i !== -1) this.multiSelectList.splice(i, 1);
				} else {
					this.multiSelectList.push(item);
				}
				this.multiSelectList = this.multiSelectList.slice();
				break;
			}

			case 'replace':
				// Held rather than returned: the replacement is confirmed from the footer.
				this.replacementProduct = item;
				break;

			default:
				this.modal.close(item);
				break;
		}
	}

	isRecentlyAddedProduct(id) {
		return this.addedProducts.some(item => item.id === id);
	}

	getAddedProducts(id) {
		return this.addedProducts.find(item => item.id === id);
	}

	selectSource(source) {
		this.selectedCatalogue = null;
		this.source = source;

		if (this.source === 'linked-catalogues' || this.source === 'my-catalogues') {
			this.app.tutorial?.validateStep(504);
		} else {
			this.app.tutorial?.invalidateStep(504);
		}

		// The basket view lists what has already been picked, so there is nothing to fetch for it.
		if (source === 'selected') return;

		// The global search holds its own term, filters, results and paging, so none of the below applies to it.
		if (source === 'global-search') {
			this.productSearch.loadBrowse();
			this.productSearch.run();
			return;
		}

		this.data = null;

		this.catalogues = null;
		this.filterCategoryGroupId = null;
		this.filterCategoryId = null;
		this.refresh();
	}

	isSourceVisible(id) {
		return this.sources.some(source => source.id === id && source.show);
	}

	/**
	 * Whether the organisation has catalogues of its own, which is what decides if My Catalogues is on offer.
	 *
	 * Asked on open rather than picked up as a side effect of listing products: the global search doesn't list from
	 * a catalogue, so nothing would ask while it is the active source, and the row of sources changed shape the
	 * moment you moved off it.
	 */
	private resolveOwnCatalogues() {
		if (this.ownCatalogueCount !== null) {
			this.setOwnCatalogueCount(this.ownCatalogueCount);
			return;
		}

		this.api.catalogue.listMyCatalogues(list => this.setOwnCatalogueCount((list || []).length));
	}

	private setOwnCatalogueCount(count) {
		this.ownCatalogueCount = count;

		const source = this.sources.find(item => item.id === 'my-catalogues');
		if (source) source.show = count > 0;
	}

	/**
	 * Takes a product picked from the global search. The distributor copy is what was chosen - price, stock and
	 * pack size are all per distributor - but the rest of this modal, and every screen it reports to, works on the
	 * product rows `product.list` returns, quote-contextual prices included. So the copy is read back in full
	 * first, and from there it goes through the same selectItem as a row clicked in any other source.
	 */
	addFromGlobalSearch(distributor) {
		const id = distributor?.product_id;
		if (!id || this.resolvingProducts.indexOf(id) !== -1) return;

		// Already in the basket: the Add button is its own undo, so a second click takes it back out.
		const picked = this.isMulti ? Mangler.findOne(this.multiSelectList, { id }) : null;
		if (picked) {
			this.selectItem(picked);
			return;
		}

		this.resolvingProducts.push(id);

		this.api.product.list(this.resolveOptions(distributor), data => {
			this.releaseProduct(id);

			// The search can offer a copy this picker can't take - a group member that fails one of its
			// constraints - and there is nothing to hand on if it does.
			if (!data.list?.length) {
				this.app.notifications.showDanger('This product cannot be added here.');
				return;
			}

			this.selectItem(data.list[0]);
		}, error => {
			this.releaseProduct(id);
			this.app.notifications.showDanger(error.message);
		});
	}

	/**
	 * How to read one distributor's copy back. A copy carries the catalogue it came from, which is exactly what
	 * product.list needs to find it: no catalogue means one of the organisation's own products.
	 */
	private resolveOptions(distributor) {
		const options: any = {
			product_id: distributor.product_id,
			mode: distributor.catalogue_id ? 'linked-catalogues' : 'my-products',
			include_categories: 0,
			sold_to_customer: 1,
			// The search lists partly-discontinued groups, so a copy being dropped can't stop it being read back.
			discontinued: -1
		};

		if (distributor.catalogue_id) options.catalogue_id = distributor.catalogue_id;
		if (this.addAccessory) options.is_bundle = 0;
		if (this.costPriceField) options.cost_price_field = this.costPriceField;
		if (this.sellPriceField) options.sell_price_field = this.sellPriceField;
		if (this.quoteId) options.quote_id = this.quoteId;

		return options;
	}

	private releaseProduct(id) {
		const index = this.resolvingProducts.indexOf(id);
		if (index !== -1) this.resolvingProducts.splice(index, 1);
	}

	viewAll() {
		this.filterCategoryGroupId = null;
		this.filterCategoryId = null;
		this.refreshProducts();
	}

	selectGroup(item) {
		this.filterCategoryGroupId = item.id;
		this.filterCategoryId = null;
		this.refreshProducts();
	}

	selectCategory(item) {
		this.filterCategoryGroupId = null;
		this.filterCategoryId = item.id;
		this.refreshProducts();
	}

	isCategoryGroupExpanded(id) {
		return !!Mangler.first(this.expandedCategoryGroups, id);
	}

	setCategoryGroupExpanded(id, state) {
		if (state) {
			if (!Mangler.first(this.expandedCategoryGroups, id)) this.expandedCategoryGroups.push(id);
		} else {
			Mangler.filter(this.expandedCategoryGroups, { $ne: id });
		}
		this.expandedCategoryGroups = this.expandedCategoryGroups.slice();
	}

	addProductsButton() {
		if (this.multiSelectList.length) {
			this.modal.close(this.multiSelectList);
		}
	}

	isItemSelected(item) {
		return !!Mangler.findOne(this.multiSelectList, { id: item.id });
	}

}
